import * as React from "react";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import { useTheme } from "@mui/material/styles";
import { Box, Skeleton, Stack, Typography } from "@mui/material";
import { Dot } from "../UIElements/svgIcons";
import { useNavigate } from "react-router-dom";
import { headerSty, contentSty } from "../table-styles";
import { formatDate, formatTextType } from "../../utils/url-id";

export default function ApplicantsTableList({ data, loading }: any) {
  const theme = useTheme();

  const headStyles = { ...headerSty, color: theme.palette.neutral.main };
  const contentStyles = { ...contentSty };

  const [openRow, setOpenRow] = React.useState<number | null>(null); // Track which row is open

  const handleToggleOpen = (rowId: number) => {
    setOpenRow((prevOpenRow) => (prevOpenRow === rowId ? null : rowId));
  };

  return (
    <TableContainer
      component={Paper}
      sx={{
        my: 1,
        background: theme.palette.common.white,
        borderRadius: "16px",
        border: 0,
        paddingBottom: 4,
        boxShadow: "none",
      }}
    >
      <Table
        sx={{
          minWidth: 650,
          boxShadow: "none",
          my: 2,
          paddingRight: 2,
          border: 0,
        }}
        aria-label="simple table"
      >
        <TableHead sx={{ paddingX: 2, border: 0 }}>
          <TableRow>
            <TableCell sx={{ ...headStyles }}>S/N</TableCell>
            <TableCell sx={{ ...headStyles }}>Candidate Name</TableCell>
            <TableCell sx={{ ...headStyles }}>Facility Info</TableCell>
            <TableCell sx={{ ...headStyles }}>Medic Type</TableCell>
            <TableCell sx={{ ...headStyles }}>Date</TableCell>
            <TableCell sx={{ ...headStyles }}>Status</TableCell>
            {/* <TableCell sx={{ ...headStyles, width: "8%" }}>Action</TableCell> */}
          </TableRow>
        </TableHead>
        <TableBody>
          {!loading && data.map((row: any, index: number) => (
            <TableRow key={row?._id}>
              <TableCell sx={contentStyles}>{index + 1}</TableCell>
              <TableCell sx={{ ...contentStyles }}>
                {row?.medic_info?.full_name ?? row?.medic_info?.name}
              </TableCell>
              <TableCell sx={{ ...contentStyles }}>
                {row?.facility_info?.name}
              </TableCell>
              <TableCell sx={{ ...contentStyles }}>
                {formatTextType(row?.medic_info?.medic_type)}
              </TableCell>
              <TableCell sx={{ ...contentStyles }}>
                {formatDate(row?.created_at)}
              </TableCell>
              <TableCell sx={{ ...contentStyles }}><ApplicationEmpStatus status={row.status} /></TableCell>
              {/* <TableCell sx={{ ...contentStyles }}>
                <TableDots candidateId={row?._id} />
              </TableCell> */}
            </TableRow>
          ))}
        </TableBody>
      </Table>
      {data?.length === 0 && !loading && (
        <Box
          sx={{
            height: 100,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <Typography
            variant="h4"
            sx={{ fontWeight: 600, mt: 5, color: theme.palette.neutral.main }}
          >
            No Applicants.
          </Typography>
        </Box>
      )}
      {loading && (
        <Box sx={{ width: "96%", mx: "auto" }}>
          <Skeleton
            variant="rounded"
            width={"100%"}
            height={40}
            sx={{ my: 2 }}
          />
          <Skeleton
            variant="rounded"
            width={"100%"}
            height={40}
            sx={{ my: 2 }}
          />
        </Box>
      )}
    </TableContainer>
  );
}

const TableDots: React.FC<{
  candidateId: string;
}> = ({ candidateId }) => {
  const [open, setOpen] = React.useState(false);
  const navigate = useNavigate();
  const handleOpen = () => {
    setOpen(!open);
  };
  return (
    <Box
      sx={{
        position: "relative",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
      }}
      onClick={handleOpen}
    >
      <Box
        sx={{
          cursor: "pointer",
          width: "20px",
        }}
      >
        <Dot />
      </Box>
      {open && (
        <Box
          sx={{
            background: "#b4c2e9",
            position: "absolute",
            top: 2,
            right: 30,
            borderRadius: "12px",
            padding: "8px 12px",
            zIndex: 10,
            width: 90,
            cursor: "pointer",
          }}
        >
          <Stack
            sx={{ my: 1 }}
            onClick={() => navigate(`/job-board/candidate/${candidateId}`)}
          >
            <Typography
              variant="caption"
              sx={{
                fontWeight: 700,
                color: "#1E1E1E",
              }}
            >
              View
            </Typography>
          </Stack>
          <Stack
            sx={{ my: 1 }}
            onClick={() => navigate(`/job-board/candidate/${candidateId}`)}
          >
            <Typography
              variant="caption"
              sx={{
                fontWeight: 700,
                color: "#1E1E1E",
              }}
            >
              Hire Candidate
            </Typography>
          </Stack>
        </Box>
      )}
    </Box>
  );
};


export const ApplicationEmpStatus: React.FC<{ status: 'pending' | 'closed' | 'active' | string }> = ({ status }) => {
  const theme = useTheme();

  const getStatusStyles = (status: string) => {
    switch (status) {
      case 'pending':
        return {
          color: theme.palette.warning.main,
          backgroundColor: '#ffac336b'
        };
      case 'closed':
        return {
          color: theme.palette.error.main,
          backgroundColor: '#f6685e6b',
        };
      case 'active':
        return {
          color: theme.palette.primary.main,
          backgroundColor: theme.palette.lightBlue.main,
        };
      default:
        return {

          color: theme.palette.success.main,
          backgroundColor: theme.palette.success.light,
        };
    }
  };

  const statusStyles = getStatusStyles(status);

  return (
    <Stack
      direction="row"
      justifyContent="center"
      sx={{ width: 100, mx: 'auto' }}
    >
      <Box
        sx={{
          marginX: 1,
          paddingY: 1,
          paddingX: 3,
          borderRadius: "3px",
          fontSize: 12,
          fontFamily: 'Lexend',
          textTransform: 'uppercase',
          ...statusStyles,
        }}
      >
        {status}
      </Box>
    </Stack>
  );
};
